import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sidebar } from '../../components/Sidebar';
import { BarChart3, IndianRupee, Briefcase, Users, Award, Building2, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPlatform = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/platform-stats`, { withCredentials: true });
        setStats(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
        <Sidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
        </div>
      </div>
    );
  }

  const cards = [
    { label: 'Total Brands', value: stats?.total_brands || 0, icon: Building2, color: '#0055FF' },
    { label: 'Total Creators', value: stats?.total_creators || 0, icon: Users, color: '#FF451A' },
    { label: 'Total Managers', value: stats?.total_managers || 0, icon: Award, color: '#00C972' },
    { label: 'Incoming Briefs', value: stats?.incoming_campaigns || 0, icon: Briefcase, color: '#FFB800' },
    { label: 'Active Campaigns', value: stats?.active_campaigns || 0, icon: TrendingUp, color: '#0055FF' },
    { label: 'Total GMV', value: `₹${(stats?.total_gmv || 0).toLocaleString()}`, icon: IndianRupee, color: '#FF451A' },
  ];

  const creatorSplit = (stats?.total_creators || 1);
  const exclPct = ((stats?.exclusive_creators || 0) / creatorSplit) * 100;
  const mktPct = ((stats?.marketplace_creators || 0) / creatorSplit) * 100;

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D] text-[#0F0F0F] dark:text-white transition-colors">
      <Sidebar />
      <div className="flex-1 p-8" data-testid="admin-platform-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
              <BarChart3 className="text-[#FF451A]" size={32} /> Platform Analytics
            </h1>
            <p className="text-[#6B6B6B] dark:text-[#8A8F98]">High-level metrics across the Vyvre ecosystem.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.label}
                  className="bg-[#F7F7F8] dark:bg-[#131518] border border-black/[0.08] dark:border-white/[0.06] rounded-2xl p-6 shadow-sm dark:shadow-none hover:-translate-y-[1px] transition-all"
                  data-testid={`platform-stat-${c.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${c.color}15` }}
                  >
                    <Icon size={20} style={{ color: c.color }} />
                  </div>
                  <div className="text-3xl font-bold text-[#0F0F0F] dark:text-white mb-1">{c.value}</div>
                  <div className="text-sm text-[#6B6B6B] dark:text-[#8A8F98]">{c.label}</div>
                </div>
              );
            })}
          </div>

          <div className="bg-[#F7F7F8] dark:bg-[#131518] border border-black/[0.08] dark:border-white/[0.06] rounded-2xl p-6 shadow-sm dark:shadow-none">
            <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-4">Creator Distribution</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2 text-sm">
                  <span className="text-[#6B6B6B] dark:text-[#8A8F98] flex items-center gap-1"><Award size={12} /> Exclusive (Managed)</span>
                  <span className="font-semibold text-[#0F0F0F] dark:text-white">{stats?.exclusive_creators || 0}</span>
                </div>
                <div className="w-full h-3 bg-black/[0.05] dark:bg-[#0A0B0D] rounded-full overflow-hidden">
                  <div className="h-full bg-[#0F0F0F] dark:bg-white rounded-full" style={{ width: `${exclPct}%` }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2 text-sm">
                  <span className="text-[#6B6B6B] dark:text-[#8A8F98]">Marketplace</span>
                  <span className="font-semibold text-[#0F0F0F] dark:text-white">{stats?.marketplace_creators || 0}</span>
                </div>
                <div className="w-full h-3 bg-black/[0.05] dark:bg-[#0A0B0D] rounded-full overflow-hidden">
                  <div className="h-full bg-[#FF451A] rounded-full" style={{ width: `${mktPct}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
